// Typed, validated wrapper around Vite's import.meta.env.
//
// Mirrors packages/admin/src/config/env.ts. Consumer SPA reads its own
// Cognito pool config; the actual pool (admin pool vs the dedicated
// consumer pool) is decided at build time by which env values the CI
// pipeline injects.
//
// For the staging deploy ahead of the investor demo, this points at the
// consumer Cognito pool that already exists in Terraform (see
// project_tenants_architecture memory). The /auth/me Lambda pool
// multiplex is C.2.d and unblocks once that lands.

interface ConsumerEnv {
  apiBaseUrl: string;
  cognitoRegion: string;
  cognitoUserPoolId: string;
  cognitoAppClientId: string;
  envLabel: string;
  /** Optional. When set, the Coach composer uses SSE streaming via a Lambda
   *  Function URL (POST .../messages/stream) and falls back to the non-
   *  streaming `/me/coach/messages` endpoint when absent. Shipped as
   *  optional so CI deploys without the var still produce a working build. */
  coachStreamUrl?: string;
  /** Consumer dictation feature flag (V1). Off by default; when true, the
   *  Coach composer renders a mic button and wires the Transcribe streaming
   *  flow. Requires `dictationIdentityPoolId` to also be set. */
  dictationEnabled: boolean;
  /** Cognito Identity Pool ID (format: eu-west-1:xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)
   *  used by the browser SDK to exchange the user's consumer-pool id_token for
   *  short-lived AWS credentials scoped to Transcribe streaming. Required when
   *  `dictationEnabled` is true. */
  dictationIdentityPoolId?: string;
}

function required(name: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(`Missing required env var ${name}. See packages/consumer/.env.example.`);
  }
  return value;
}

const raw = import.meta.env;

const coachStreamUrlRaw = raw.VITE_COACH_STREAM_URL?.trim();
const dictationEnabledRaw = raw.VITE_DICTATION_ENABLED?.trim().toLowerCase();
const dictationIdentityPoolRaw = raw.VITE_DICTATION_IDENTITY_POOL_ID?.trim();
const dictationEnabled = dictationEnabledRaw === 'true' || dictationEnabledRaw === '1';

if (dictationEnabled && !dictationIdentityPoolRaw) {
  throw new Error(
    'VITE_DICTATION_ENABLED=true but VITE_DICTATION_IDENTITY_POOL_ID is not set. ' +
      'Set the Cognito Identity Pool ID from `terraform output -raw dictation_identity_pool_id` ' +
      'or turn the feature off.',
  );
}

export const env: ConsumerEnv = {
  apiBaseUrl: required('VITE_API_BASE_URL', raw.VITE_API_BASE_URL).replace(/\/$/, ''),
  cognitoRegion: required('VITE_COGNITO_REGION', raw.VITE_COGNITO_REGION),
  cognitoUserPoolId: required('VITE_COGNITO_USER_POOL_ID', raw.VITE_COGNITO_USER_POOL_ID),
  cognitoAppClientId: required('VITE_COGNITO_APP_CLIENT_ID', raw.VITE_COGNITO_APP_CLIENT_ID),
  envLabel: raw.VITE_ENV_LABEL ?? 'local',
  ...(coachStreamUrlRaw ? { coachStreamUrl: coachStreamUrlRaw.replace(/\/$/, '') } : {}),
  dictationEnabled,
  ...(dictationIdentityPoolRaw ? { dictationIdentityPoolId: dictationIdentityPoolRaw } : {}),
};
